import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategories, useDocs } from '../hooks/useDocs';
import { api } from '../api/client';
import { getToken, clearToken, isLoggedIn } from '../utils/auth';
import type { DocEntry, AuditLog, DocDetail, DocVersionDetail } from '../api/types';
import { parseMarkdown, resolveHtmlAssetPaths, attachCodeBlockCopy } from '../utils/markdown';
import readerStyles from '../components/modules/DocReader.module.css';
import CyberpunkPhotoManager from '../components/admin/CyberpunkPhotoManager';
import styles from './Admin.module.css';

type SortKey = 'title' | 'date' | 'category' | 'status';
type AdminView = 'docs' | 'recycle' | 'logs' | 'photos';

// 自定义确认框状态（替代 window.confirm）
interface ConfirmState {
  title?: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
}

// 文档预览请求：recycle=回收站预览，log=操作日志预览（version 为日志记录的版本号）
interface PreviewRequest {
  kind: 'recycle' | 'log';
  slug: string;
  title: string;
  version?: number | null;
}

export default function Admin() {
  const navigate = useNavigate();
  const { categories } = useCategories();
  const { docs, loading, refetch } = useDocsWithRefetch();
  const [filter, setFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [editDoc, setEditDoc] = useState<DocEntry | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ── View switching (docs / recycle / logs) ─────────────────
  const [view, setView] = useState<AdminView>('docs');
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

  // ── Recycle Bin state ──────────────────────────────────────
  const [recycleDocs, setRecycleDocs] = useState<DocEntry[]>([]);
  const [recycleLoading, setRecycleLoading] = useState(false);
  const [recycleSelected, setRecycleSelected] = useState<Set<string>>(new Set());

  // ── Audit Logs state ───────────────────────────────────────
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logOffset, setLogOffset] = useState(0);
  const [logLimit, setLogLimit] = useState(20);

  // ── Doc preview state（回收站/操作日志预览弹窗）────────────
  const [preview, setPreview] = useState<PreviewRequest | null>(null);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  // 加载回收站列表
  const loadRecycleBin = async () => {
    setRecycleLoading(true);
    try {
      const token = getToken()!;
      const data = await api.listRecycleBin(token);
      setRecycleDocs(data);
    } catch (err) {
      showMessage('error', err instanceof Error ? err.message : '加载回收站失败');
    } finally {
      setRecycleLoading(false);
    }
  };

  // 加载操作日志（分页）
  const loadLogs = async (offset: number, limit: number) => {
    setLogsLoading(true);
    try {
      const token = getToken()!;
      const data = await api.listAuditLogs({ limit, offset }, token);
      setLogs(data);
      setLogOffset(offset);
    } catch (err) {
      showMessage('error', err instanceof Error ? err.message : '加载操作日志失败');
    } finally {
      setLogsLoading(false);
    }
  };

  // 切换标签页时加载对应数据
  useEffect(() => {
    if (view === 'recycle') loadRecycleBin();
    else if (view === 'logs') loadLogs(logOffset, logLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Redirect if not logged in
  if (!isLoggedIn()) {
    return (
      <div className={styles.admin}>
        <div style={{ padding: 60, textAlign: 'center' }}>
          <h2>ACCESS DENIED</h2>
          <p>Please login first.</p>
          <button className={styles.btn} onClick={() => navigate('/')}>← BACK TO LOGIN</button>
        </div>
      </div>
    );
  }

  const filtered = (() => {
    let list = [...docs];
    if (filter) {
      const q = filter.toLowerCase();
      list = list.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.slug.toLowerCase().includes(q) ||
          d.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    if (catFilter) {
      list = list.filter((d) => d.category === catFilter);
    }
    list.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'title') cmp = a.title.localeCompare(b.title);
      else if (sortKey === 'date') cmp = a.date.localeCompare(b.date);
      else if (sortKey === 'category') cmp = a.category.localeCompare(b.category);
      else if (sortKey === 'status') cmp = a.status.localeCompare(b.status);
      return sortAsc ? cmp : -cmp;
    });
    return list;
  })();

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const toggleSelect = (slug: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((d) => d.slug)));
  };

  // 批量软删除正式文章（自定义 Modal 确认）
  const handleDelete = () => {
    if (selected.size === 0) return;
    const slugs = [...selected];
    setConfirmState({
      title: '确认软删除',
      message: `确认删除 ${slugs.length} 篇文章？\n${slugs.join('\n')}`,
      confirmLabel: '删除',
      danger: true,
      onConfirm: async () => {
        setActionLoading(true);
        const token = getToken()!;
        let success = 0;
        let failed = 0;
        for (const slug of slugs) {
          try {
            await api.deleteDoc(slug, token);
            success++;
          } catch {
            failed++;
          }
        }
        setActionLoading(false);
        setSelected(new Set());
        if (success > 0) showMessage('success', `成功删除 ${success} 篇文章`);
        if (failed > 0) showMessage('error', `${failed} 篇删除失败`);
        refetch();
        setConfirmState(null);
      },
    });
  };

  // ── Recycle Bin handlers ───────────────────────────────────
  const toggleRecycleSelect = (slug: string) => {
    setRecycleSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleRecycleAll = () => {
    if (recycleSelected.size === recycleDocs.length) setRecycleSelected(new Set());
    else setRecycleSelected(new Set(recycleDocs.map((d) => d.slug)));
  };

  // 还原单篇文档
  const handleRestore = async (slug: string) => {
    setActionLoading(true);
    try {
      const token = getToken()!;
      await api.restoreDoc(slug, token);
      showMessage('success', '文档已还原');
      setRecycleSelected(new Set());
      await loadRecycleBin();
      refetch();
    } catch (err) {
      showMessage('error', err instanceof Error ? err.message : '还原失败');
    } finally {
      setActionLoading(false);
    }
  };

  // 单篇彻底删除（自定义 Modal 确认）
  const handlePurgeOne = (slug: string, title: string) => {
    setConfirmState({
      title: '彻底删除',
      message: `确认彻底删除「${title}」？此操作不可恢复！`,
      confirmLabel: '彻底删除',
      danger: true,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const token = getToken()!;
          await api.purgeDoc(slug, token);
          showMessage('success', '文档已彻底删除');
          setRecycleSelected(new Set());
          await loadRecycleBin();
        } catch (err) {
          showMessage('error', err instanceof Error ? err.message : '彻底删除失败');
        } finally {
          setActionLoading(false);
          setConfirmState(null);
        }
      },
    });
  };

  // 批量彻底删除（自定义 Modal 确认）
  const handleBatchPurge = () => {
    if (recycleSelected.size === 0) return;
    const slugs = [...recycleSelected];
    setConfirmState({
      title: '批量彻底删除',
      message: `确认彻底删除 ${slugs.length} 篇文档？此操作不可恢复！\n${slugs.join('\n')}`,
      confirmLabel: '彻底删除',
      danger: true,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const token = getToken()!;
          const res = await api.batchPurgeDocs(slugs, token);
          showMessage('success', `已彻底删除 ${res.purged} 篇文档`);
          setRecycleSelected(new Set());
          await loadRecycleBin();
        } catch (err) {
          showMessage('error', err instanceof Error ? err.message : '批量彻底删除失败');
        } finally {
          setActionLoading(false);
          setConfirmState(null);
        }
      },
    });
  };

  // ── Audit Logs handlers ────────────────────────────────────
  const goLogsPrev = () => loadLogs(Math.max(0, logOffset - logLimit), logLimit);
  const goLogsNext = () => loadLogs(logOffset + logLimit, logLimit);
  const changeLogLimit = (limit: number) => {
    setLogLimit(limit);
    loadLogs(0, limit);
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('zh-CN', { hour12: false });
  };

  const actionLabel = (action: string) => {
    const map: Record<string, string> = {
      create: '创建',
      update: '更新',
      delete: '删除',
      restore: '还原',
      purge: '彻底删除',
      publish: '发布',
      upload: '上传',
      rollback: '回滚',
    };
    return map[action] || action;
  };

  // 操作类型徽章样式
  const actionBadgeClass = (action: string) => {
    if (action === 'create') return styles.statusPublished;
    if (action === 'update' || action === 'publish' || action === 'upload' || action === 'rollback') {
      return styles.statusDraft;
    }
    return styles.statusArchived;
  };

  const renderStatus = (status: string) => {
    const cls =
      status === 'published'
        ? styles.statusPublished
        : status === 'draft'
          ? styles.statusDraft
          : styles.statusArchived;
    const label =
      status === 'published' ? '已发布' : status === 'draft' ? '草稿' : status === 'archived' ? '已归档' : status;
    return <span className={`${styles.statusBadge} ${cls}`}>{label}</span>;
  };

  const handleLogout = () => {
    clearToken();
    navigate('/');
  };

  const handleQuickPublish = async (slug: string) => {
    setActionLoading(true);
    try {
      const token = getToken()!;
      await api.updateDoc(slug, { status: 'published' }, token);
      showMessage('success', '文章已快捷发布');
      refetch();
    } catch (err) {
      showMessage('error', err instanceof Error ? err.message : '发布失败');
    } finally {
      setActionLoading(false);
    }
  };

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return '↕';
    return sortAsc ? '↑' : '↓';
  };

  const catMap = new Map(categories.map((c) => [c.slug, { icon: c.icon, title: c.title }]));
  const catIcon = (slug: string) => catMap.get(slug)?.icon || '📄';
  const catName = (slug: string) => catMap.get(slug)?.title || slug;

  if (loading) {
    return <div className={styles.admin}><div style={{ padding: 40, textAlign: 'center' }}>LOADING...</div></div>;
  }

  return (
    <div className={styles.admin}>
      {message && (
        <div className={`${styles.toast} ${message.type === 'error' ? styles.toastError : styles.toastSuccess}`}>
          {message.text}
        </div>
      )}

      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>ADMIN PANEL</h1>
            <span className={styles.count}>{filtered.length} / {docs.length} docs</span>
          </div>
          <div className={styles.headerRight}>
            <button className={styles.btn} onClick={() => navigate('/')}>← HOME</button>
            <button className={styles.btn} onClick={handleLogout}>LOGOUT</button>
          </div>
        </header>

        <div className={styles.brutalPanel}>
          <div className={styles.toolbar}>
            <div className={styles.viewTabs}>
              <button
                className={`${styles.viewTab} ${view === 'docs' ? styles.viewTabActive : ''}`}
                onClick={() => setView('docs')}
              >
                DOCS
              </button>
              <button
                className={`${styles.viewTab} ${view === 'recycle' ? styles.viewTabActive : ''}`}
                onClick={() => setView('recycle')}
              >
                回收站
              </button>
              <button
                className={`${styles.viewTab} ${view === 'logs' ? styles.viewTabActive : ''}`}
                onClick={() => setView('logs')}
              >
                操作日志
              </button>
              <button
                className={`${styles.viewTab} ${view === 'photos' ? styles.viewTabActive : ''}`}
                onClick={() => setView('photos')}
              >
                图库
              </button>
            </div>

            {view === 'docs' && (
              <>
                <div className={styles.searchWrap}>
                  <span className={styles.searchPrompt}>&gt;</span>
                  <input
                    className={styles.search}
                    type="text"
                    placeholder="SEARCH / TITLE / SLUG / TAGS..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                </div>
                <select className={styles.select} value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
                  <option value="">全部分类</option>
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.icon} {c.title}</option>
                  ))}
                </select>
                <button className={styles.btnPrimary} onClick={() => setShowAdd(true)}>+ NEW RECORD</button>
                <button className={styles.btnDanger} disabled={selected.size === 0 || actionLoading} onClick={handleDelete}>
                  {actionLoading ? 'DELETING...' : `DELETE (${selected.size})`}
                </button>
              </>
            )}

            {view === 'recycle' && (
              <>
                <span className={styles.count}>{recycleDocs.length} in recycle bin</span>
                <button
                  className={styles.btnDanger}
                  disabled={recycleSelected.size === 0 || actionLoading}
                  onClick={handleBatchPurge}
                >
                  {actionLoading ? 'PURGING...' : `彻底删除 (${recycleSelected.size})`}
                </button>
              </>
            )}
          </div>

          {view === 'photos' && <CyberpunkPhotoManager onMessage={showMessage} />}

          {view === 'docs' && (
          <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thCheck}>
                <label className={styles.checkboxWrap}>
                  <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} />
                  <span className={styles.checkmark}></span>
                </label>
              </th>
              <th onClick={() => toggleSort('title')} className={styles.thSortable}>标题 {sortIcon('title')}</th>
              <th onClick={() => toggleSort('category')} className={styles.thSortable}>分类 {sortIcon('category')}</th>
              <th onClick={() => toggleSort('date')} className={styles.thSortable}>日期 {sortIcon('date')}</th>
              <th onClick={() => toggleSort('status')} className={styles.thSortable}>状态 {sortIcon('status')}</th>
              <th>标签</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((doc) => (
              <tr key={doc.slug} className={selected.has(doc.slug) ? styles.rowSelected : ''}>
                <td className={styles.tdCheck}>
                  <label className={styles.checkboxWrap}>
                    <input type="checkbox" checked={selected.has(doc.slug)} onChange={() => toggleSelect(doc.slug)} />
                    <span className={styles.checkmark}></span>
                  </label>
                </td>
                <td className={styles.tdTitle}>
                  <span className={styles.docTitle}>{doc.title}</span>
                  <span className={styles.docSlug}>{doc.slug}</span>
                </td>
                <td><span className={styles.catBadge}>{catIcon(doc.category)} {catName(doc.category)}</span></td>
                <td className={styles.tdDate}>{doc.date}</td>
                <td>{renderStatus(doc.status)}</td>
                <td className={styles.tdTags}>
                  <div className={styles.tagsWrapper}>
                    {doc.tags.slice(0, 3).map((t) => <span key={t} className={styles.tag}>{t}</span>)}
                    {doc.tags.length > 3 && <span className={styles.tagMore}>+{doc.tags.length - 3}</span>}
                  </div>
                </td>
                <td>
                  <button className={styles.btnSmall} onClick={() => setEditDoc(doc)}>编辑</button>
                  {doc.status !== 'published' && (
                    <button 
                      className={`${styles.btnSmall} ${styles.btnPublish}`}
                      onClick={() => handleQuickPublish(doc.slug)}
                    >
                      发布
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
          </div>
          )}

          {view === 'recycle' && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.thCheck}>
                    <label className={styles.checkboxWrap}>
                      <input
                        type="checkbox"
                        checked={recycleSelected.size === recycleDocs.length && recycleDocs.length > 0}
                        onChange={toggleRecycleAll}
                      />
                      <span className={styles.checkmark}></span>
                    </label>
                  </th>
                  <th>标题</th>
                  <th>分类</th>
                  <th>日期</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {recycleLoading ? (
                  <tr><td colSpan={6} className={styles.loadingRow}>加载回收站...</td></tr>
                ) : recycleDocs.length === 0 ? (
                  <tr><td colSpan={6} className={styles.loadingRow}>回收站为空</td></tr>
                ) : recycleDocs.map((doc) => (
                  <tr key={doc.slug} className={recycleSelected.has(doc.slug) ? styles.rowSelected : ''}>
                    <td className={styles.tdCheck}>
                      <label className={styles.checkboxWrap}>
                        <input
                          type="checkbox"
                          checked={recycleSelected.has(doc.slug)}
                          onChange={() => toggleRecycleSelect(doc.slug)}
                        />
                        <span className={styles.checkmark}></span>
                      </label>
                    </td>
                    <td className={styles.tdTitle}>
                      <span className={styles.docTitle}>{doc.title}</span>
                      <span className={styles.docSlug}>{doc.slug}</span>
                    </td>
                    <td><span className={styles.catBadge}>{catIcon(doc.category)} {catName(doc.category)}</span></td>
                    <td className={styles.tdDate}>{doc.date}</td>
                    <td>{renderStatus(doc.status)}</td>
                    <td>
                      <button
                        className={styles.btnSmall}
                        onClick={() => setPreview({ kind: 'recycle', slug: doc.slug, title: doc.title })}
                        disabled={actionLoading}
                      >
                        预览
                      </button>
                      <button
                        className={`${styles.btnSmall} ${styles.btnPublish}`}
                        onClick={() => handleRestore(doc.slug)}
                        disabled={actionLoading}
                      >
                        还原
                      </button>
                      <button
                        className={`${styles.btnSmall} ${styles.btnPurge}`}
                        onClick={() => handlePurgeOne(doc.slug, doc.title)}
                        disabled={actionLoading}
                      >
                        彻底删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {view === 'logs' && (
          <div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>操作者</th>
                    <th>操作类型</th>
                    <th>文档标题</th>
                    <th>版本</th>
                    <th>描述</th>
                    <th>时间</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {logsLoading ? (
                    <tr><td colSpan={7} className={styles.loadingRow}>加载操作日志...</td></tr>
                  ) : logs.length === 0 ? (
                    <tr><td colSpan={7} className={styles.loadingRow}>暂无操作日志</td></tr>
                  ) : logs.map((log, idx) => (
                    <tr key={`${log.doc_slug}-${log.created_at}-${log.version}-${idx}`}>
                      <td>{log.actor}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${actionBadgeClass(log.action)}`}>
                          {actionLabel(log.action)}
                        </span>
                      </td>
                      <td className={styles.tdTitle}>
                        <span className={styles.docTitle}>{log.doc_title}</span>
                        {log.doc_slug && <span className={styles.docSlug}>{log.doc_slug}</span>}
                      </td>
                      <td className={styles.tdDate}>{log.version != null ? `v${log.version}` : '-'}</td>
                      <td className={styles.logDesc}>{log.description}</td>
                      <td className={styles.tdDate}>{formatTime(log.created_at)}</td>
                      <td>
                        <button
                          className={styles.btnSmall}
                          onClick={() =>
                            setPreview({
                              kind: 'log',
                              slug: log.doc_slug,
                              title: log.doc_title,
                              version: log.version,
                            })
                          }
                        >
                          查看
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.pagination}>
              <button
                className={styles.btnSmall}
                onClick={goLogsPrev}
                disabled={logOffset === 0 || logsLoading}
              >
                ← PREV
              </button>
              <span className={styles.pageInfo}>OFFSET {logOffset}</span>
              <button
                className={styles.btnSmall}
                onClick={goLogsNext}
                disabled={logs.length < logLimit || logsLoading}
              >
                NEXT →
              </button>
              <select
                className={styles.select}
                value={logLimit}
                onChange={(e) => changeLogLimit(Number(e.target.value))}
              >
                <option value={10}>10 / 页</option>
                <option value={20}>20 / 页</option>
                <option value={50}>50 / 页</option>
              </select>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {showAdd && (
        <AddDocModal
          categories={categories}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); refetch(); showMessage('success', '文章创建成功'); }}
          onError={(msg) => showMessage('error', msg)}
        />
      )}

      {/* Edit Modal */}
      {editDoc && (
        <EditDocModal
          doc={editDoc}
          categories={categories}
          onClose={() => setEditDoc(null)}
          onSaved={() => { setEditDoc(null); refetch(); showMessage('success', '文章更新成功'); }}
          onError={(msg) => showMessage('error', msg)}
        />
      )}

      {/* Confirm Modal (replaces window.confirm) */}
      {confirmState && (
        <ConfirmModal state={confirmState} onClose={() => setConfirmState(null)} />
      )}

      {/* Doc Preview Modal（回收站预览 / 操作日志查看与回滚） */}
      {preview && (
        <DocPreviewModal
          request={preview}
          onClose={() => setPreview(null)}
          onRolledBack={() => {
            setPreview(null);
            showMessage('success', '已回滚到指定版本');
            loadLogs(logOffset, logLimit);
            refetch();
          }}
          onError={(msg) => showMessage('error', msg)}
        />
      )}
    </div>
  );
}

// ── Doc Preview Modal（回收站预览 / 操作日志查看 + 版本回滚）──
function DocPreviewModal({ request, onClose, onRolledBack, onError }: {
  request: PreviewRequest;
  onClose: () => void;
  onRolledBack: () => void;
  onError: (msg: string) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [data, setData] = useState<{
    title: string;
    tags: string[];
    status: string;
    content: string;
  } | null>(null);
  const [sourceLabel, setSourceLabel] = useState('');
  const [rollbackTarget, setRollbackTarget] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [rolling, setRolling] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const apply = (
      d: { title: string; tags: string[]; status: string; content: string },
      label: string,
      target: number | null,
    ) => {
      if (cancelled) return;
      setData(d);
      setSourceLabel(label);
      setRollbackTarget(target);
    };

    const run = async () => {
      const token = getToken()!;
      const fromDetail = (d: DocDetail) => ({
        title: d.title, tags: d.tags, status: d.status, content: d.content,
      });
      try {
        if (request.kind === 'recycle') {
          // 回收站预览：优先读软删文档；已被还原则回退普通详情
          try {
            const d = await api.getDeletedDoc(request.slug, token);
            apply(fromDetail(d), '回收站中内容（删除时状态）', null);
          } catch {
            const d = await api.getDoc(request.slug);
            apply(fromDetail(d), '当前内容（文档可能已被还原）', null);
          }
          return;
        }

        // 操作日志预览：优先版本快照 → 当前内容 → 回收站内容
        let snapshot: DocVersionDetail | null = null;
        if (request.version != null) {
          try {
            snapshot = await api.getDocVersion(request.slug, request.version, token);
          } catch { /* 快照不存在或已清理 */ }
        }
        let current: DocDetail | null = null;
        try {
          current = await api.getDoc(request.slug);
        } catch { /* 文档已删除或不存在 */ }

        if (snapshot && current) {
          apply(
            { title: snapshot.title, tags: snapshot.tags, status: snapshot.status, content: snapshot.content },
            current.version === snapshot.version
              ? '该版本即当前内容'
              : `版本 v${snapshot.version} 快照（${snapshot.actor} 保存）`,
            request.version ?? null,
          );
        } else if (snapshot) {
          apply(
            { title: snapshot.title, tags: snapshot.tags, status: snapshot.status, content: snapshot.content },
            `版本 v${snapshot.version} 快照（文档当前不可回滚）`,
            null,
          );
        } else if (current) {
          const exact = request.version != null && current.version === request.version;
          apply(
            fromDetail(current),
            exact ? '当前内容（即该版本状态）' : '版本快照不存在或已清理，展示当前内容',
            null,
          );
        } else {
          try {
            const d = await api.getDeletedDoc(request.slug, token);
            apply(fromDetail(d), '文档已在回收站中（删除时内容）', null);
          } catch {
            if (!cancelled) setLoadError('无法加载文档内容（文档或版本快照不存在）');
          }
        }
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : '加载预览失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const html = useMemo(
    () => (data ? resolveHtmlAssetPaths(parseMarkdown(data.content).content, request.slug) : ''),
    [data, request.slug],
  );

  // Attach copy-button handlers to rendered code blocks in the preview
  useEffect(() => {
    if (!previewRef.current) return;
    return attachCodeBlockCopy(previewRef.current);
  }, [html]);

  // 回滚到预览的版本快照（服务端会先自动保存当前内容为新快照）
  const doRollback = async () => {
    if (rollbackTarget == null) return;
    setRolling(true);
    try {
      await api.rollbackDoc(request.slug, rollbackTarget, getToken()!);
      onRolledBack();
    } catch (err) {
      onError(err instanceof Error ? err.message : '回滚失败');
      setConfirming(false);
    } finally {
      setRolling(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.previewModal}`} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>{data?.title || request.title}</h2>
        {loading ? (
          <div className={styles.loadingRow}>加载预览...</div>
        ) : loadError ? (
          <div className={styles.loadingRow}>{loadError}</div>
        ) : data ? (
          <>
            <div className={styles.previewMeta}>
              <span>{sourceLabel}</span>
              <span>状态：{data.status}</span>
              {data.tags.length > 0 && <span>标签：{data.tags.join(' / ')}</span>}
            </div>
            <div className={styles.previewScroll}>
              <div
                ref={previewRef}
                className={readerStyles.content}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </div>
            <div className={styles.modalActions}>
              {rollbackTarget != null && !confirming && (
                <button className={styles.btnPrimary} onClick={() => setConfirming(true)}>
                  回滚到此版本 (v{rollbackTarget})
                </button>
              )}
              {confirming && (
                <>
                  <span className={styles.previewWarn}>回滚前会自动保存当前内容为新快照，确认执行？</span>
                  <button className={styles.btn} onClick={() => setConfirming(false)} disabled={rolling}>
                    取消
                  </button>
                  <button className={styles.btnDanger} onClick={doRollback} disabled={rolling}>
                    {rolling ? '回滚中...' : `确认回滚 (v${rollbackTarget})`}
                  </button>
                </>
              )}
              <button className={styles.btn} onClick={onClose}>关闭</button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// ── Reusable Confirm Modal ───────────────────────────────────
function ConfirmModal({ state, onClose }: {
  state: ConfirmState;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await state.onConfirm();
    } catch {
      /* 错误由调用方处理 */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={busy ? undefined : onClose}>
      <div className={`${styles.modal} ${styles.confirmModal}`} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>{state.title || '确认操作'}</h2>
        <div className={styles.confirmMessage}>{state.message}</div>
        <div className={styles.confirmActions}>
          <button className={styles.btn} onClick={onClose} disabled={busy}>取消</button>
          <button
            className={state.danger ? styles.btnDanger : styles.btnPrimary}
            onClick={handleConfirm}
            disabled={busy}
          >
            {busy ? '处理中...' : (state.confirmLabel || '确认')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helper: useDocs with refetch capability ──────────────────
function useDocsWithRefetch() {
  const { docs: initialDocs, loading } = useDocs();
  const [docs, setDocs] = useState<DocEntry[]>(initialDocs);

  // Sync initial load
  if (initialDocs.length > 0 && docs.length === 0) {
    setDocs(initialDocs);
  }

  const refetch = async () => {
    try {
      const data = await api.listDocs();
      setDocs(data);
    } catch { /* ignore */ }
  };

  return { docs, loading, refetch };
}

// ── Attachment type ──────────────────────────────────────────
interface Attachment {
  name: string;
  file: string;   // auto-generated path like "assets/photo_20260529101800.jpg"
  desc: string;
  url: string;
}

// ── Add Document Modal ───────────────────────────────────────
function AddDocModal({ categories, onClose, onCreated, onError }: {
  categories: Array<{ slug: string; icon: string; title: string }>;
  onClose: () => void;
  onCreated: () => void;
  onError: (msg: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(categories[0]?.slug || '');
  const [tags, setTags] = useState('');
  const [status, setStatus] = useState('draft');
  const [content, setContent] = useState('# New Document\n\nStart writing here...');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Auto-generated slug from category + title
  const docSlug = `${category}/${title.trim().toLowerCase().replace(/[^a-z0-9\u4e00-\u9fff]+/g, '-').replace(/^-|-$/g, '')}`;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (!title.trim()) { alert('请先填写标题'); return; }

    setUploading(true);
    const token = getToken()!;

    for (const file of Array.from(files)) {
      try {
        // Read file as base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Strip data:xxx;base64, prefix
            resolve(result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const res = await api.uploadFile(docSlug, file.name, base64, token);
        setAttachments((prev) => [...prev, {
          name: file.name,
          file: res.path,
          desc: '',
          url: res.url,
        }]);
      } catch (err) {
        alert(`上传失败: ${file.name} - ${err instanceof Error ? err.message : '未知错误'}`);
      }
    }

    setUploading(false);
    // Reset file input
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title.trim()) { onError('标题不能为空'); return; }
    setSaving(true);
    try {
      const token = getToken()!;
      // Build content with frontmatter attachments
      let fullContent = content;
      if (attachments.length > 0) {
        const yaml = attachments.map((a) => `  - name: ${a.name}\n    file: ${a.file}\n    desc: ${a.desc}\n    url: ${a.url}`).join('\n');
        // Inject attachments into frontmatter if content starts with ---
        if (fullContent.startsWith('---')) {
          fullContent = fullContent.replace('---\n', `---\nattachments:\n${yaml}\n`);
        }
      }
      await api.createDoc({
        title: title.trim(),
        category,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        status,
        content: fullContent,
      }, token);
      onCreated();
    } catch (err) {
      onError(err instanceof Error ? err.message : '创建失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>新增文章</h2>
        <div className={styles.modalBody}>
          {/* Left column: metadata */}
          <div className={styles.modalLeft}>
            <div className={styles.formGroup}>
              <label>标题</label>
              <input className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="文章标题" />
            </div>
            <div className={styles.formGroup}>
              <label>分类</label>
              <select className={styles.select} value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.icon} {c.title}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>标签（逗号分隔）</label>
              <input className={styles.input} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tag1, tag2, tag3" />
            </div>
            <div className={styles.formGroup}>
              <label>状态</label>
              <select className={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="draft">草稿</option>
                <option value="published">已发布</option>
              </select>
            </div>

            {/* Attachments */}
            <div className={styles.formGroup}>
              <label>附件 / 资源</label>
              <div className={styles.attachHint}>
                存储路径: <code>{docSlug || '（请先填写标题）'}/assets/</code>
              </div>
              {attachments.length > 0 && (
                <div className={styles.attachList}>
                  {attachments.map((a, i) => (
                    <div key={i} className={styles.attachItem}>
                      <span className={styles.attachItemName}>{a.name}</span>
                      <span className={styles.attachItemDesc}>{a.file}</span>
                      <button className={styles.attachRemove} onClick={() => removeAttachment(i)} title="移除">✕</button>
                    </div>
                  ))}
                </div>
              )}
              <label className={styles.uploadBtn}>
                {uploading ? '上传中...' : '📎 选择文件'}
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={uploading || !title.trim()}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>

          {/* Right column: content editor */}
          <div className={styles.modalRight}>
            <div className={styles.formGroup} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <label>内容（Markdown + Frontmatter）</label>
              <textarea
                className={styles.textarea}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                style={{ flex: 1, minHeight: 400, resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className={styles.modalActions}>
            <button className={styles.btn} onClick={onClose}>取消</button>
            <button className={styles.btnPrimary} onClick={handleSubmit} disabled={saving}>
              {saving ? '创建中...' : '创建'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Edit Document Modal ──────────────────────────────────────
function EditDocModal({ doc, categories, onClose, onSaved, onError }: {
  doc: DocEntry;
  categories: Array<{ slug: string; icon: string; title: string }>;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [title, setTitle] = useState(doc.title);
  const [category, setCategory] = useState(doc.category);
  const [tags, setTags] = useState(doc.tags.join(', '));
  const [status, setStatus] = useState(doc.status);
  const [content, setContent] = useState('');
  const [contentLoading, setContentLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachName, setAttachName] = useState('');
  const [attachFile, setAttachFile] = useState('');
  const [attachDesc, setAttachDesc] = useState('');

  // Load current content + parse attachments from frontmatter
  useEffect(() => {
    api.getDoc(doc.slug).then(({ content: c }) => {
      setContent(c);
      // Try to parse attachments from the YAML frontmatter
      const match = c.match(/^---[\s\S]*?attachments:\n([\s\S]*?)\n---/);
      if (match) {
        try {
          // Simple parse: each attachment block
          const blocks = match[1].split(/\n  - /).filter(Boolean);
          const parsed: Attachment[] = blocks.map((block) => {
            const nameM = block.match(/name:\s*(.+)/);
            const fileM = block.match(/file:\s*(.+)/);
            const descM = block.match(/desc:\s*(.+)/);
            const urlM = block.match(/url:\s*(.+)/);
            return {
              name: nameM?.[1]?.trim() || '',
              file: fileM?.[1]?.trim() || '',
              desc: descM?.[1]?.trim() || '',
              url: urlM?.[1]?.trim() || '',
            };
          });
          setAttachments(parsed);
        } catch { /* ignore parse errors */ }
      }
      setContentLoading(false);
    }).catch(() => {
      setContent('# Error loading content');
      setContentLoading(false);
    });
  }, [doc.slug]);

  const addAttachment = () => {
    if (!attachFile.trim()) return;
    const name = attachName.trim() || attachFile.split('/').pop() || 'file';
    const url = `/api/files/${doc.slug}/${attachFile.trim()}`;
    setAttachments([...attachments, { name, file: attachFile.trim(), desc: attachDesc.trim(), url }]);
    setAttachName('');
    setAttachFile('');
    setAttachDesc('');
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title.trim()) { onError('标题不能为空'); return; }
    setSaving(true);
    try {
      const token = getToken()!;
      await api.updateDoc(doc.slug, {
        title: title.trim(),
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        status,
        content: content || undefined,
      }, token);
      onSaved();
    } catch (err) {
      onError(err instanceof Error ? err.message : '更新失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>编辑文章</h2>
        <div className={styles.modalBody}>
          {/* Left column: metadata */}
          <div className={styles.modalLeft}>
            <div className={styles.formGroup}>
              <label>标题</label>
              <input className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label>分类</label>
              <select className={styles.select} value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.icon} {c.title}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>标签（逗号分隔）</label>
              <input className={styles.input} value={tags} onChange={(e) => setTags(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label>状态</label>
              <select className={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="draft">草稿</option>
                <option value="published">已发布</option>
                <option value="archived">已归档</option>
              </select>
            </div>

            {/* Attachments */}
            <div className={styles.formGroup}>
              <label>附件 / 资源</label>
              {attachments.length > 0 && (
                <div className={styles.attachList}>
                  {attachments.map((a, i) => (
                    <div key={i} className={styles.attachItem}>
                      <span className={styles.attachItemName}>{a.name}</span>
                      <span className={styles.attachItemDesc}>{a.desc || a.file}</span>
                      <button className={styles.attachRemove} onClick={() => removeAttachment(i)} title="移除">✕</button>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.attachAddRow}>
                <input className={styles.input} value={attachName} onChange={(e) => setAttachName(e.target.value)} placeholder="名称" style={{ flex: '0.8' }} />
                <input className={styles.input} value={attachFile} onChange={(e) => setAttachFile(e.target.value)} placeholder="文件路径 如 assets/SKILL.md" style={{ flex: '1.5' }} />
                <input className={styles.input} value={attachDesc} onChange={(e) => setAttachDesc(e.target.value)} placeholder="描述" style={{ flex: '1' }} />
                <button className={styles.btnSmall} onClick={addAttachment} style={{ alignSelf: 'stretch' }}>添加</button>
              </div>
            </div>
          </div>

          {/* Right column: content editor */}
          <div className={styles.modalRight}>
            <div className={styles.formGroup} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <label>内容（Markdown + Frontmatter）</label>
              {contentLoading ? (
                <div style={{ padding: 40, textAlign: 'center' }}>加载内容中...</div>
              ) : (
                <textarea
                  className={styles.textarea}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  style={{ flex: 1, minHeight: 400, resize: 'vertical' }}
                />
              )}
            </div>
          </div>

          {/* Actions */}
          <div className={styles.modalActions}>
            <button className={styles.btn} onClick={onClose}>取消</button>
            <button className={styles.btnPrimary} onClick={handleSubmit} disabled={saving || contentLoading}>
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
